/**
 * Reducer paired with the notifications system. 'ApplicationDataPush' message
 * updates the value in this reducer. Components subscribe to the value in this
 * reducer and if the value indicates the component should update, it does so.
 * If your application doesn't use the notifications system to send messages
 * other than stack notifications there is no need to include this in your app.
 */
import { Store } from "./rootReducer";

// Identifiers can be found in Stoplight project under dummy notifications endpoint.
export enum NotificationIdentifier {
  NEW_PENDING_GUARANTEE = "NEW_PENDING_GUARANTEE",
}

export type NotificationData = {
  pushType: NotificationIdentifier.NEW_PENDING_GUARANTEE;
};

/**
 * Only a single update is stored which means that if it is not consumed immediately
 * it could get overwritten by a future update. This is fine because if the update
 * is not consumed immediately it means the target component isn't rendered and when
 * it does get rerendered, the latest data will be fetched anyway. This approach
 * means that any data sent in a SignalR message must also be reflected in the REST
 * API call when it gets made.
 */
type NotificationUIUpdateState = {
  update: NotificationData | null;
};

const initialState: NotificationUIUpdateState = { update: null };

enum NotificationUIUpdateActionType {
  ADD_UI_UPDATE = "ADD_UI_UPDATE",
  COMPLETE_UI_UPDATE = "COMPLETE_UI_UPDATE",
}

type NotificationUIUpdateAction =
  | {
      type: NotificationUIUpdateActionType.ADD_UI_UPDATE;
      payload: NotificationData;
    }
  | { type: NotificationUIUpdateActionType.COMPLETE_UI_UPDATE };

const Reducer = (
  state = initialState,
  action: NotificationUIUpdateAction
): NotificationUIUpdateState => {
  switch (action.type) {
    case NotificationUIUpdateActionType.ADD_UI_UPDATE:
      return { update: action.payload };
    case NotificationUIUpdateActionType.COMPLETE_UI_UPDATE:
      return { update: null };
    default:
      return state;
  }
};

export default Reducer;

// Action creators

export const setUIUpdate = (
  payload: NotificationData
): NotificationUIUpdateAction => {
  return {
    type: NotificationUIUpdateActionType.ADD_UI_UPDATE,
    payload: payload,
  };
};
export const completeUIUpdate = (): NotificationUIUpdateAction => ({
  type: NotificationUIUpdateActionType.COMPLETE_UI_UPDATE,
});

// Selectors
export const selectUIUpdate = (store: Store) => store.notification.update;
